import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Switch,
  Divider,
  FormControlLabel,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import SmsIcon from '@mui/icons-material/Sms';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import PriceChangeIcon from '@mui/icons-material/PriceChange';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { useNavigate } from 'react-router-dom';

const Notifications: React.FC = () => {
  const [prefs, setPrefs] = useState({
    push: true,
    email: true,
    sms: false,
    price: true,
    transaction: true,
    promo: true,
    weekly: true,
    sound: true,
  });
  const [permission, setPermission] = useState<'Blocked' | 'Allowed'>('Blocked');
  const navigate = useNavigate();

  const handleSwitch = (name: keyof typeof prefs) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setPrefs({ ...prefs, [name]: e.target.checked });
  };

  return (
    <Box sx={{ p: 2, pb: { xs: 12, sm: 12, md: 12 }, bgcolor: '#f5f6fa', minHeight: '100vh' }}>
      {/* Header with Split Watermark - Left: NSFLogo, Right: Empty (for future custom logo) */}
      <Box sx={{ 
        position: 'relative', 
        mb: 3,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -50,
          left: -50,
          width: '100%',
          height: '200px',
          background: `url('/NSFIcon.png')`,
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          opacity: 0.08,
          zIndex: 0,
          pointerEvents: 'none'
        }
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          position: 'relative',
          zIndex: 1
        }}>
          <Box
            onClick={() => navigate('/profile')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              color: 'text.primary',
              mr: 2,
              '&:hover': { opacity: 0.7 }
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: 20, mr: 0.5 }} />
            <Typography variant="body2" fontWeight="500">Back</Typography>
          </Box>
        </Box>

        <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ position: 'relative', zIndex: 1 }}>
          Notifications
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, position: 'relative', zIndex: 1 }}>
          Manage your notification preferences
        </Typography>
      </Box>

      {/* Browser Notifications */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 3, bgcolor: '#f8fafc', border: '1px solid #e0e0e0' }}>
        <Typography fontWeight={600} mb={1}>
          <NotificationsActiveIcon sx={{ color: '#1A5FB4', mr: 1, mb: '-4px' }} />
          Browser Notifications
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography fontSize={14} color="#757575">Permission Status:</Typography>
          <Typography fontWeight={600} color={permission === 'Allowed' ? 'green' : 'red'}>{permission}</Typography>
        </Box>
        <Button variant="contained" size="small" sx={{ bgcolor: '#1A5FB4', fontWeight: 600 }} onClick={() => setPermission('Allowed')}>
          Enable
        </Button>
      </Paper>

      {/* Notification Types */}
      <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
        <Typography fontWeight={600} mb={1}>
          <NotificationsActiveIcon sx={{ color: '#4caf50', mr: 1, mb: '-4px' }} />
          Notification Types
        </Typography>
        {/* Delivery Methods */}
        <Typography fontWeight={600} fontSize={15} mt={2} mb={1}>Delivery Methods</Typography>
        <FormControlLabel
          control={<Switch checked={prefs.push} onChange={handleSwitch('push')} />}
          label={<Box sx={{ display: 'flex', alignItems: 'center' }}><NotificationsActiveIcon sx={{ mr: 1 }} />Push Notifications</Box>}
        />
        <Typography fontSize={13} color="#757575" mb={1} ml={5}>Receive instant notifications in your browser</Typography>
        <FormControlLabel
          control={<Switch checked={prefs.email} onChange={handleSwitch('email')} />}
          label={<Box sx={{ display: 'flex', alignItems: 'center' }}><MarkEmailReadIcon sx={{ mr: 1 }} />Email Notifications</Box>}
        />
        <Typography fontSize={13} color="#757575" mb={1} ml={5}>Receive notifications via email</Typography>
        <FormControlLabel
          control={<Switch checked={prefs.sms} onChange={handleSwitch('sms')} />}
          label={<Box sx={{ display: 'flex', alignItems: 'center' }}><SmsIcon sx={{ mr: 1 }} />SMS Notifications</Box>}
        />
        <Typography fontSize={13} color="#757575" mb={1} ml={5}>Receive text messages for important alerts</Typography>
        <Divider sx={{ my: 2 }} />
        {/* What You'll Receive */}
        <Typography fontWeight={600} fontSize={15} mb={1}>What You'll Receive</Typography>
        <FormControlLabel
          control={<Switch checked={prefs.price} onChange={handleSwitch('price')} />}
          label={<Box sx={{ display: 'flex', alignItems: 'center' }}><PriceChangeIcon sx={{ mr: 1 }} />Fuel Price Alerts</Box>}
        />
        <Typography fontSize={13} color="#757575" mb={1} ml={5}>Get notified when fuel prices drop at nearby stations</Typography>
        <FormControlLabel
          control={<Switch checked={prefs.transaction} onChange={handleSwitch('transaction')} />}
          label={<Box sx={{ display: 'flex', alignItems: 'center' }}><ReceiptLongIcon sx={{ mr: 1 }} />Transaction Alerts</Box>}
        />
        <Typography fontSize={13} color="#757575" mb={1} ml={5}>Confirmation when you use your fuel card</Typography>
        <FormControlLabel
          control={<Switch checked={prefs.promo} onChange={handleSwitch('promo')} />}
          label={<Box sx={{ display: 'flex', alignItems: 'center' }}><LocalOfferIcon sx={{ mr: 1 }} />Promotional Offers</Box>}
        />
        <Typography fontSize={13} color="#757575" mb={1} ml={5}>Special deals and discounts from partner stations</Typography>
        <FormControlLabel
          control={<Switch checked={prefs.weekly} onChange={handleSwitch('weekly')} />}
          label={<Box sx={{ display: 'flex', alignItems: 'center' }}><MarkEmailReadIcon sx={{ mr: 1 }} />Weekly Reports</Box>}
        />
        <Typography fontSize={13} color="#757575" mb={1} ml={5}>Summary of your fuel savings and usage</Typography>
        <Divider sx={{ my: 2 }} />
        <FormControlLabel
          control={<Switch checked={prefs.sound} onChange={handleSwitch('sound')} />}
          label={<Box sx={{ display: 'flex', alignItems: 'center' }}><VolumeUpIcon sx={{ mr: 1 }} />Notification Sounds</Box>}
        />
        <Typography fontSize={13} color="#757575" mb={2} ml={5}>Play sounds with notifications</Typography>
        <Button variant="contained" sx={{ bgcolor: '#1A5FB4', fontWeight: 600, mt: 1 }} fullWidth>
          Save Preferences
        </Button>
      </Paper>
    </Box>
  );
};

export default Notifications; 